/**
 * New Chat Modal Component
 * 
 * Modal for starting new conversations (direct or group).
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  List,
  Avatar,
  Checkbox,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  Tabs,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { User, ChatScope } from '@municipality/types';
import { chatService } from '@municipality/chat';

const { Text } = Typography;

/**
 * Get initials from a name
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

const UserAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor?: string }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 500 !important;
  font-size: 13px !important;
`;

const SmallUserAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor?: string }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 500 !important;
  font-size: 11px !important;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 24px;
`;

const UserList = styled(List<User>)`
  max-height: 400px;
  overflow: auto;
`;

const GroupUserList = styled(List<User>)`
  max-height: 300px;
  overflow: auto;
`;

const ClickableListItem = styled(List.Item)`
  cursor: pointer;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserEmail = styled(Text)`
  font-size: 12px;
`;

interface NewChatModalProps {
  visible: boolean;
  currentUserId: string;
  scope: ChatScope;
  municipalityId?: string;
  onClose: () => void;
  onCreateDirectChat: (recipientId: string) => void;
  onCreateGroupChat: (name: string, participantIds: string[]) => void;
}

export const NewChatModal: React.FC<NewChatModalProps> = ({
  visible,
  currentUserId,
  scope,
  municipalityId,
  onClose,
  onCreateDirectChat,
  onCreateGroupChat,
}) => {
  const { t } = useTranslation('chat');
  const [activeTab, setActiveTab] = useState('direct');
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load available users
  const loadUsers = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const availableUsers = await chatService.getAvailableUsers(
        currentUserId,
        scope,
        municipalityId
      );
      setUsers(availableUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, scope, municipalityId]);

  // Load available users
  useEffect(() => {
    if (visible) {
      loadUsers();
    }
  }, [visible, loadUsers]);

  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    );
  });

  const handleUserSelect = (userId: string) => {
    if (activeTab === 'direct') {
      onCreateDirectChat(userId);
      handleClose();
    } else {
      // Toggle selection for group chat
      setSelectedUsers((prev) =>
        prev.includes(userId)
          ? prev.filter((id) => id !== userId)
          : [...prev, userId]
      );
    }
  };

  const handleCreateGroup = () => {
    if (!groupName.trim() || selectedUsers.length < 2) {
      return;
    }
    onCreateGroupChat(groupName.trim(), selectedUsers);
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedUsers([]);
    setGroupName('');
    setActiveTab('direct');
    onClose();
  };

  return (
    <Modal
      title={t('newChatModal.title')}
      open={visible}
      onCancel={handleClose}
      footer={
        activeTab === 'group' ? (
          <Space>
            <Button onClick={handleClose}>{t('common.cancel', { ns: 'common' })}</Button>
            <Button
              type="primary"
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || selectedUsers.length < 2}
            >
              {t('newChatModal.createGroup')}
            </Button>
          </Space>
        ) : null
      }
      width={500}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'direct',
            label: t('newChatModal.directMessage'),
            children: (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input
                  placeholder={t('newChatModal.searchPlaceholder')}
                  prefix={<SearchOutlined />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  aria-label={t('newChatModal.searchPlaceholder')}
                />

                {isLoading ? (
                  <LoadingContainer>
                    <Spin />
                  </LoadingContainer>
                ) : filteredUsers.length === 0 ? (
                  <Empty description={t('newChatModal.noUsersFound')} />
                ) : (
                  <UserList
                    dataSource={filteredUsers}
                    renderItem={(user: User) => (
                      <ClickableListItem onClick={() => handleUserSelect(user.id)}>
                        <List.Item.Meta
                          avatar={
                            <UserAvatar
                              src={user.photoURL}
                              size={40}
                              $bgColor={user.photoURL ? undefined : getAvatarColor(user.displayName || user.email)}
                            >
                              {!user.photoURL && getInitials(user.displayName || user.email)}
                            </UserAvatar>
                          }
                          title={user.displayName || user.email}
                          description={user.email}
                        />
                      </ClickableListItem>
                    )}
                  />
                )}
              </Space>
            ),
          },
          {
            key: 'group',
            label: t('newChatModal.groupChat'),
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Input
                  placeholder={t('newChatModal.groupNamePlaceholder')}
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />

                <Input
                  placeholder={t('newChatModal.searchPlaceholder')}
                  prefix={<SearchOutlined />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  aria-label={t('newChatModal.searchPlaceholder')}
                />

                <Text type="secondary">
                  {t('newChatModal.selectParticipants', { count: selectedUsers.length })}
                </Text>

                {isLoading ? (
                  <LoadingContainer>
                    <Spin />
                  </LoadingContainer>
                ) : filteredUsers.length === 0 ? (
                  <Empty description={t('newChatModal.noUsersFound')} />
                ) : (
                  <GroupUserList
                    dataSource={filteredUsers}
                    renderItem={(user: User) => (
                      <List.Item>
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelect(user.id)}
                        >
                          <Space>
                            <SmallUserAvatar
                              src={user.photoURL}
                              size="small"
                              $bgColor={user.photoURL ? undefined : getAvatarColor(user.displayName || user.email)}
                            >
                              {!user.photoURL && getInitials(user.displayName || user.email)}
                            </SmallUserAvatar>
                            <UserInfo>
                              <div>{user.displayName || user.email}</div>
                              <UserEmail type="secondary">
                                {user.email}
                              </UserEmail>
                            </UserInfo>
                          </Space>
                        </Checkbox>
                      </List.Item>
                    )}
                  />
                )}
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
};

